/**
 * The state codec (tech-design §5): a pure, Decimal-safe bridge between the live
 * {@link GameState} — which carries `Num`/Decimal instances the engine operates on —
 * and a plain, JSON-safe object that survives IndexedDB structured-clone, a JSON
 * round-trip, and cross-device transfer.
 *
 * Every `Num` is encoded as a string via the engine's own `serialize`/`parse`
 * (`@core/num` stays the *only* module that touches `break_infinity`). The codec is
 * pure and I/O-free, so `deserialize(serialize(state))` is deep-equal to `state` —
 * the round-trip guarantee GAM-5 rests on — and it is unit-testable in Node.
 */
import {
  parse,
  serialize,
  type GameState,
  type AgesState,
  type BreakthroughState,
  type Meta,
  type RngState,
} from '@core/index.js';

// ── The serialized (plain, JSON-safe) mirror of GameState ─────────────────────

export interface SerializedResourceState {
  readonly amount: string;
  readonly lifetime: string;
}

export interface SerializedGeneratorState {
  readonly owned: number;
  readonly multiplier: string;
}

export interface SerializedPrestigeLayerState {
  readonly currency: string;
  readonly purchased: readonly string[];
  readonly count: number;
}

/**
 * GameState with every `Num` replaced by a Decimal-safe string. Fields that are
 * already JSON-safe (`breakthroughs`, `ages`, `rng`, `meta`, the id arrays) carry
 * through unchanged.
 */
export interface SerializedGameState {
  readonly resources: Record<string, SerializedResourceState>;
  readonly generators: Record<string, SerializedGeneratorState>;
  readonly upgrades: readonly string[];
  readonly traits: readonly string[];
  readonly milestones: readonly string[];
  readonly breakthroughs: Record<string, BreakthroughState>;
  readonly ages: AgesState;
  readonly prestige: Record<string, SerializedPrestigeLayerState>;
  readonly rng: RngState;
  readonly meta: Meta;
}

// ── Serialize: GameState → plain object ───────────────────────────────────────

function mapValues<In, Out>(
  record: Readonly<Record<string, In>>,
  fn: (value: In) => Out,
): Record<string, Out> {
  const out: Record<string, Out> = {};
  for (const [key, value] of Object.entries(record)) {
    out[key] = fn(value);
  }
  return out;
}

export function serializeState(state: GameState): SerializedGameState {
  return {
    resources: mapValues(state.resources, (r) => ({
      amount: serialize(r.amount),
      lifetime: serialize(r.lifetime),
    })),
    generators: mapValues(state.generators, (g) => ({
      owned: g.owned,
      multiplier: serialize(g.multiplier),
    })),
    upgrades: [...state.upgrades],
    traits: [...state.traits],
    milestones: [...state.milestones],
    breakthroughs: mapValues(state.breakthroughs, (b) => ({ ...b })),
    ages: { current: state.ages.current, completed: [...state.ages.completed] },
    prestige: mapValues(state.prestige, (p) => ({
      currency: serialize(p.currency),
      purchased: [...p.purchased],
      count: p.count,
    })),
    rng: { ...state.rng },
    meta: { ...state.meta },
  };
}

// ── Deserialize: plain object → GameState ─────────────────────────────────────

export function deserializeState(data: SerializedGameState): GameState {
  return {
    resources: mapValues(data.resources, (r) => ({
      amount: parse(r.amount),
      lifetime: parse(r.lifetime),
    })),
    generators: mapValues(data.generators, (g) => ({
      owned: g.owned,
      multiplier: parse(g.multiplier),
    })),
    upgrades: [...data.upgrades],
    traits: [...data.traits],
    milestones: [...data.milestones],
    breakthroughs: mapValues(data.breakthroughs, (b) => ({ ...b })),
    ages: { current: data.ages.current, completed: [...data.ages.completed] },
    prestige: mapValues(data.prestige, (p) => ({
      currency: parse(p.currency),
      purchased: [...p.purchased],
      count: p.count,
    })),
    rng: { ...data.rng },
    meta: { ...data.meta },
  };
}
